import { PaymentMethod, SetupIntentResult } from '@fox/finance';
import { UpdateRsvpStatusInputModel } from '../events/contracts';
import {
	CompleteIntentInputModel,
	CreateSubscriptionInputModel,
	DeletePaymentMethodInputModel,
	PayInvoiceModelInputModel,
	PayInvoiceResult,
	Receipt,
	SetupIntentInputModel,
	SwiftFoxPaymentInputModel,
	SwiftFoxSubscription,
	SwiftFoxSubscriptionResult,
	UpdateCreditCardExpiryInputModel,
	UpdateSubscriptionInputModel,
	UpdateSubscriptionPaymentMethodInputModel
} from '../financial-details/contracts';
import { DeleteGoalActivityInputModel, GetSasInputModel, UpsertGoalActivityInputModel } from '../goals/contracts';
import {
	CreateImportBatchInputModel,
	CreateImportProfileInputModel,
	DeleteImportProfileInputModel,
	UpsertImportDocInputModel
} from '../importer/contracts';
import { CreateOrgMembershipInputModel, RemoveOrgMembershipInputModel } from '../representatives/contracts';
import { DeletePaymentMethodMutationHookProps, MutationHookProps, useMutation } from './mutation-template';

export const useCompleteCardIntentMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<number, CompleteIntentInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CompleteCardIntent`
	});

	return { data, loading, error, mutate };
};

export const useCompleteDebitIntentMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<number, CompleteIntentInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CompleteDebitIntent`
	});

	return { data, loading, error, mutate };
};

export const useCreateSubscriptionMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<SwiftFoxSubscriptionResult, CreateSubscriptionInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CreateSubscription`
	});

	return { data, loading, error, mutate };
};
export const useSetupIntentMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<SetupIntentResult, SetupIntentInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/SetupIntent`
	});

	return { data, loading, error, mutate };
};

export const useUpdateCreditCardExpiryMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, UpdateCreditCardExpiryInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/UpdateCreditCard`
	});

	return { data, loading, error, mutate };
};

export const useDeletePaymentMethodMutation = (input: DeletePaymentMethodMutationHookProps) => {
	const url =
		input.paymentMethod === PaymentMethod.CreditCard
			? '/Umbraco/Api/Membership/DeleteCreditCard'
			: input.paymentMethod === PaymentMethod.DirectDebit
			? `/Umbraco/Api/Membership/DeleteDirectDebit`
			: '/Umbraco/Api/Membership/DeleteCreditCard';

	const { data, loading, error, mutate } = useMutation<boolean, DeletePaymentMethodInputModel>({
		...input,
		url
	});

	return { data, loading, error, mutate };
};

export const useUpdateSubscriptionMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<SwiftFoxSubscription, UpdateSubscriptionInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/UpdateSubscription`
	});

	return { data, loading, error, mutate };
};

export const useUpdateSubscriptionPaymentMethodMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<SwiftFoxSubscription, UpdateSubscriptionPaymentMethodInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/UpdateSubscriptionPaymentMethod`
	});

	return { data, loading, error, mutate };
};

export const useUpdateRsvpStatusMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, UpdateRsvpStatusInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/RsvpToEvent`
	});

	return { data, loading, error, mutate };
};

export const useMakePaymentMutation = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<Receipt, SwiftFoxPaymentInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/MakePayment`
	});

	return { data, loading, error, mutate };
};

export const useRemoveOrgMembership = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, RemoveOrgMembershipInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/RemoveOrgMembership`
	});

	return { data, loading, error, mutate };
};

export const useCreateOrgMembership = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, CreateOrgMembershipInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CreateOrgMembership`
	});

	return { data, loading, error, mutate };
};

export const useDeleteImportProfile = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<null, DeleteImportProfileInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/DeleteImportProfile`
	});

	return { data, loading, error, mutate };
};

export const useCreateImportProfile = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<null, CreateImportProfileInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CreateImportProfile`
	});

	return { data, loading, error, mutate };
};

export const useCreateImportBatch = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, CreateImportBatchInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CreateImportBatch`
	});

	return { data, loading, error, mutate };
};

export const useUpsertImportDoc = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, UpsertImportDocInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/UpsertImportDoc`
	});

	return { data, loading, error, mutate };
};

export const usePayInvoice = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<PayInvoiceResult, PayInvoiceModelInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/PayInvoice`
	});

	return { data, loading, error, mutate };
};

export const useUpsertGoalActivity = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, UpsertGoalActivityInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/UpsertGoalActivity`
	});

	return { data, loading, error, mutate };
};

export const useGoalsAttachmentSas = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<string, GetSasInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/GetGoalAttachmentSas`
	});

	return { data, loading, error, mutate };
};

export const useDeleteGoalActivity = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<boolean, DeleteGoalActivityInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/DeleteGoalActivity`
	});

	return { data, loading, error, mutate };
};

export const useCustomFieldFileFieldSas = (input: MutationHookProps) => {
	const { data, loading, error, mutate } = useMutation<string, GetSasInputModel>({
		...input,
		url: `/Umbraco/Api/Membership/CustomFieldFileSas`
	});

	return { data, loading, error, mutate };
};